<script setup lang="ts">
/**
 * Confirm Dialog
 * Mount once in App.vue — driven by useConfirm() composable.
 */
import { onMounted, onUnmounted } from 'vue'
import { TriangleAlert } from 'lucide-vue-next'
import { confirmState, handleConfirm, handleCancel } from '@/composables/useConfirm'

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && confirmState.visible) {
    handleCancel()
  }
}

function onOverlayClick(e: MouseEvent) {
  if ((e.target as HTMLElement) === e.currentTarget) {
    handleCancel()
  }
}

onMounted(() => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="animate-fade-in"
      leave-active-class="animate-fade-out"
    >
      <div
        v-if="confirmState.visible"
        class="fixed inset-0 bg-black/60 backdrop-blur-[4px] z-[10002] flex items-center justify-center p-5"
        @click="onOverlayClick"
      >
        <div
          class="bg-surface rounded-lg shadow-lg border border-border max-w-[400px] w-full p-8 text-center animate-modal-slide-in"
          role="dialog"
          aria-modal="true"
        >
          <!-- Icon -->
          <div
            :class="[
              'w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-full',
              confirmState.options.variant === 'warning'
                ? 'bg-yellow-500/10 text-amber-600'
                : 'bg-red-500/10 text-accent-red',
            ]"
          >
            <TriangleAlert :size="48" :stroke-width="1.5" class="overflow-visible" />
          </div>

          <h3 class="text-xl font-semibold mb-3 text-foreground">
            {{ confirmState.options.title }}
          </h3>
          <p class="text-sm text-foreground-secondary mb-6 leading-relaxed">
            {{ confirmState.options.message }}
          </p>

          <div class="flex gap-3 justify-center">
            <button class="btn btn-secondary min-w-[100px]" @click="handleCancel">
              {{ confirmState.options.cancelText }}
            </button>
            <button
              :class="[
                'btn min-w-[100px]',
                confirmState.options.variant === 'danger' ? 'btn-danger' : 'btn-primary',
              ]"
              @click="handleConfirm"
            >
              {{ confirmState.options.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
